import { ReactElement, useMemo, useRef, useState } from 'react';
import {
    CompanyUser,
    UserGuardianNotification,
    UserGuardianNotifications
} from '@/api';
import {
    UIIcon,
    UIMenu,
    UIMenuItem,
    UIPopover,
    UIPropoverRef,
    UISection,
    UISectionContent,
    UISectionFooter,
    UISectionHeader,
    UISectionTableHeader,
    UIText,
    UITooltip
} from '@/ui';
import { charcoal, concrete } from '@/ui/styles';
import { useUpdateCompanyUserAction } from '../actions';
import { useCompanyContext } from '../providers';
import { useCompaniesTranslation } from '../hooks';
import { isEqual } from 'lodash';
import styles from './member-guardian-notifications-settings-section.module.scss';

type GuardianNotificationsSettingsSectionProps = {
    companyUser: CompanyUser;
    canEdit: boolean;
    /** Shown on the disabled Edit button when `canEdit` is false. */
    editDisabledReason?: string;
};

// The eight guardian notifications, in the order the team page lists them.
const NOTIFICATION_KEYS: (keyof UserGuardianNotifications)[] = [
    'taskStarted',
    'taskExtended',
    'taskCompleted',
    'taskNote',
    'alertTriggered',
    'alertAssigned',
    'alertUnassigned',
    'alertResolved'
];

/**
 * The user's personal override of their teams' notification settings. Every
 * notification type and channel is independently either forced on, forced off,
 * or left to the team's setting (stored as null) — mirroring the API exactly,
 * one select per cell.
 */
export function MemberGuardianNotificationsSettingsSection(
    props: GuardianNotificationsSettingsSectionProps
): ReactElement {
    const { companyUser, canEdit, editDisabledReason } = props;

    const { t } = useCompaniesTranslation();
    const { company } = useCompanyContext();
    const updateCompanyUser = useUpdateCompanyUserAction({
        successMessage: t(
            'components.member_guardian_notifications_settings_section.toast_updated'
        )
    });

    const [isEditing, setIsEditing] = useState(false);
    const [guardianNotifications, setGuardianNotifications] =
        useState<UserGuardianNotifications>(companyUser.guardianNotifications);

    const hasSMSSubscription = useMemo(() => {
        if (!company.smsSubscription) {
            return false;
        }
        return ['active', 'trialing', 'past_due'].includes(
            company.smsSubscription.status
        );
    }, [company]);

    const hasChanges = useMemo(
        () =>
            !isEqual(guardianNotifications, companyUser.guardianNotifications),
        [guardianNotifications, companyUser.guardianNotifications]
    );

    function setRow(
        key: keyof UserGuardianNotifications,
        notification: UserGuardianNotification
    ) {
        setGuardianNotifications((prev) => ({
            ...prev,
            [key]: notification
        }));
    }

    function resetForm() {
        setGuardianNotifications(companyUser.guardianNotifications);
        setIsEditing(false);
    }

    function onSubmit() {
        updateCompanyUser
            .updateCompanyUser(companyUser.id, { guardianNotifications })
            .then(() => {
                setIsEditing(false);
            })
            .catch(() => {}); // Error handled in the action
    }

    return (
        <UISection>
            <UISectionHeader
                title={t(
                    'components.member_guardian_notifications_settings_section.title'
                )}
                subtitle={t(
                    'components.member_guardian_notifications_settings_section.subtitle'
                )}
                rightButtons={
                    isEditing
                        ? []
                        : [
                              {
                                  text: t('shared.labels.edit'),
                                  variant: 'textDefault',
                                  type: 'button',
                                  disabled: !canEdit,
                                  disabledTooltip: editDisabledReason,
                                  onClick: () => setIsEditing(true)
                              }
                          ]
                }
            />
            <UISectionTableHeader
                items={[
                    { text: t('shared.notifications.type_column') },
                    {
                        items: [
                            {
                                text: t('shared.notifications.app_column'),
                                tooltip: t(
                                    'shared.notifications.app_column_tooltip'
                                )
                            },
                            {
                                text: t('shared.notifications.email_column'),
                                tooltip: t(
                                    'shared.notifications.email_column_tooltip'
                                )
                            },
                            {
                                text: t('shared.notifications.sms_column'),
                                tooltip: t(
                                    'shared.notifications.sms_column_tooltip'
                                )
                            }
                        ]
                    }
                ]}
            />
            <UISectionContent>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 24
                    }}
                >
                    {NOTIFICATION_KEYS.map((key) => (
                        <NotificationRow
                            key={key}
                            label={t(`shared.notifications.${key}_label`)}
                            description={t(
                                `shared.notifications.${key}_description`
                            )}
                            notification={guardianNotifications[key]}
                            onChange={(notification) =>
                                setRow(key, notification)
                            }
                            isEditing={isEditing}
                            hasSMSSubscription={hasSMSSubscription}
                        />
                    ))}
                </div>
            </UISectionContent>
            {isEditing && (
                <UISectionFooter
                    actions={[
                        {
                            variant: 'primary',
                            type: 'button',
                            loading: updateCompanyUser.loading,
                            disabled: updateCompanyUser.loading || !hasChanges,
                            text: updateCompanyUser.loading
                                ? t('shared.labels.saving')
                                : t('shared.labels.save'),
                            onClick: onSubmit
                        },
                        {
                            text: t('shared.labels.cancel'),
                            type: 'button',
                            disabled: updateCompanyUser.loading,
                            onClick: resetForm
                        }
                    ]}
                />
            )}
        </UISection>
    );
}

function NotificationRow(props: {
    label: string;
    description: string;
    notification: UserGuardianNotification;
    onChange: (notification: UserGuardianNotification) => void;
    isEditing: boolean;
    hasSMSSubscription: boolean;
}): ReactElement {
    const {
        label,
        description,
        notification,
        onChange,
        isEditing,
        hasSMSSubscription
    } = props;

    const { t } = useCompaniesTranslation();

    function onChannelChange(
        channel: 'app' | 'email' | 'sms',
        value: boolean | null
    ) {
        onChange({
            ...notification,
            [channel]: value
        });
    }

    const smsCell = (
        <ChannelCell
            value={notification.sms}
            onChange={(value) => onChannelChange('sms', value)}
            isEditing={isEditing}
            disabled={!hasSMSSubscription}
        />
    );

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 16
            }}
        >
            <div
                style={{
                    display: 'flex',
                    flex: 1,
                    flexDirection: 'column',
                    gap: 4
                }}
            >
                <UIText>{label}</UIText>
                <UIText type="default2">{description}</UIText>
            </div>

            <div style={{ display: 'flex', flex: 1, alignItems: 'center' }}>
                <ChannelCell
                    value={notification.app}
                    onChange={(value) => onChannelChange('app', value)}
                    isEditing={isEditing}
                />
                <ChannelCell
                    value={notification.email}
                    onChange={(value) => onChannelChange('email', value)}
                    isEditing={isEditing}
                />
                {isEditing && !hasSMSSubscription ? (
                    <div style={{ display: 'flex', flex: 1 }}>
                        <UITooltip
                            text={t(
                                'shared.notifications.sms_billing_required'
                            )}
                        >
                            {smsCell}
                        </UITooltip>
                    </div>
                ) : (
                    smsCell
                )}
            </div>
        </div>
    );
}

// The three states a cell can hold, in menu order, with the stored value each
// one maps to.
const CELL_STATES: { key: 'team' | 'on' | 'off'; value: boolean | null }[] = [
    { key: 'team', value: null },
    { key: 'on', value: true },
    { key: 'off', value: false }
];

/**
 * One channel of one notification: the team's setting applies (null), or the
 * user has forced it on or off. Three states, so it can't be a switch — but a
 * boxed select is far too heavy repeated 24 times, so this is the same light
 * text-and-chevron menu trigger the section headers use.
 */
function ChannelCell(props: {
    value: boolean | null;
    onChange: (value: boolean | null) => void;
    isEditing: boolean;
    disabled?: boolean;
}): ReactElement {
    const { value, onChange, isEditing, disabled = false } = props;

    const { t } = useCompaniesTranslation();
    const popoverRef = useRef<UIPropoverRef>(null);

    const stateKey = value === null ? 'team' : value ? 'on' : 'off';
    const stateLabel = t(
        `components.member_guardian_notifications_settings_section.state_${stateKey}`
    );

    // On is blue, Off is red, inherited is muted — the same colour language as
    // the On/Off states elsewhere in the portal ('link' and 'error' types).
    const stateTextType =
        value === null ? 'disabled' : value ? 'link' : 'error';

    if (!isEditing) {
        return (
            <div style={{ display: 'flex', flex: 1 }}>
                <UIText type={stateTextType} medium={value !== null}>
                    {stateLabel}
                </UIText>
            </div>
        );
    }

    if (disabled) {
        return (
            <div style={{ display: 'flex', flex: 1 }}>
                <div className={styles.selectDisabled}>
                    <UIText type="disabled">{stateLabel}</UIText>
                    <UIIcon name="chevron-down" size={14} color={concrete} />
                </div>
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flex: 1 }}>
            <UIPopover
                ref={popoverRef}
                placement="auto"
                popoverPadding={4}
                trigger="click"
                triggerNode={
                    <button className={styles.selectButton} type="button">
                        <UIText
                            type={value === null ? 'default2' : stateTextType}
                            medium={value !== null}
                        >
                            {stateLabel}
                        </UIText>
                        <UIIcon
                            name="chevron-down"
                            size={16}
                            color={charcoal}
                        />
                    </button>
                }
            >
                <UIMenu>
                    {CELL_STATES.map((state) => (
                        <UIMenuItem
                            key={state.key}
                            text={t(
                                `components.member_guardian_notifications_settings_section.state_${state.key}`
                            )}
                            selected={state.key === stateKey}
                            small
                            onClick={() => {
                                onChange(state.value);
                                popoverRef.current?.close();
                            }}
                        />
                    ))}
                </UIMenu>
            </UIPopover>
        </div>
    );
}
